import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { journalSourceLabel } from '@accounting/shared';
import { useDrilldown } from '../../hooks/useReports';
import { CONSOLIDATED, useActiveSelection } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

/**
 * Dedicated full-page drilldown for any report figure. Replaced the
 * DrilldownModal overlay. Search params carry the account codes + label so
 * back-button works and links are shareable.
 */
export const Route = createFileRoute('/_auth/reports/drilldown')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { codes?: string; label?: string; from?: string; to?: string; back?: string } => ({
    codes: typeof search.codes === 'string' ? search.codes : undefined,
    label: typeof search.label === 'string' ? search.label : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    back: typeof search.back === 'string' ? search.back : undefined,
  }),
  component: DrilldownPage,
});

function DrilldownPage() {
  const selection = useActiveSelection();
  const search = Route.useSearch();
  const codes = (search.codes ?? '').split(',').filter(Boolean);
  const label = search.label ?? 'Drilldown';
  const [dFrom, setDFrom] = useState(search.from ?? '');
  const [dTo, setDTo] = useState(search.to ?? '');
  const consolidated = selection === CONSOLIDATED;

  const { data, isLoading, error } = useDrilldown(selection, {
    codes,
    from: dFrom || undefined,
    to: dTo || undefined,
  });

  if (!selection) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  // Back link goes to whichever report opened this drilldown, falling back to /reports.
  const backHref = (search.back ?? '/reports') as
    | '/reports'
    | '/reports/balance-sheet'
    | '/reports/income-statement'
    | '/reports/cash-flow'
    | '/reports/trial-balance'
    | '/reports/weekly-profitability';

  return (
    <div>
      <Link to={backHref} style={{ fontSize: 13 }}>← Back to report</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, margin: '8px 0 16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{label}</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
            Transactions behind this figure
            {codes.length === 1 ? ` (account ${codes[0]})` : ` (${codes.length} accounts)`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13, flexWrap: 'wrap' }}>
          <label>
            From{' '}
            <input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} />
          </label>
          <label>
            To <input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} />
          </label>
          {(dFrom || dTo) && (
            <button
              className="btn-secondary"
              style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => {
                setDFrom('');
                setDTo('');
              }}
            >
              Clear dates
            </button>
          )}
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load transactions.</p>}

      {data && data.lines.length === 0 && (
        <p className="muted" style={{ padding: '12px 0' }}>No transactions in this range.</p>
      )}

      {data && data.lines.length > 0 && (
        <div className="card" style={{ padding: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entry</th>
                <th title="What created this entry: manual, invoice, bank match, import, …">Source</th>
                {consolidated && <th>Company</th>}
                <th>Account</th>
                <th>Memo</th>
                <th style={{ textAlign: 'right' }}>Debit</th>
                <th style={{ textAlign: 'right' }}>Credit</th>
                <th
                  style={{ textAlign: 'right' }}
                  title="Account balance immediately after this line posted, signed on the account's normal side. Includes prior-period activity when a From date is set."
                >
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={`${l.journalEntryId}-${i}`}>
                  <td>{formatAccountingDate(l.date)}</td>
                  <td>
                    {consolidated ? (
                      l.entryNumber
                    ) : (
                      <Link to="/journal/$id" params={{ id: l.journalEntryId }}>
                        {l.entryNumber}
                      </Link>
                    )}
                  </td>
                  <td>
                    <span className="tag" style={{ fontSize: 11 }}>{journalSourceLabel(l.source)}</span>
                  </td>
                  {consolidated && <td>{l.companyName ?? '—'}</td>}
                  <td>
                    {l.accountCode} — {l.accountName}
                  </td>
                  <td>{l.memo ?? ''}</td>
                  <td style={{ textAlign: 'right' }}>{l.debit === '0.00' ? '' : formatCurrency(l.debit)}</td>
                  <td style={{ textAlign: 'right' }}>{l.credit === '0.00' ? '' : formatCurrency(l.credit)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {l.balanceAfter != null
                      ? formatCurrency(l.balanceAfter, true)
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
              {data.openingBalance != null && (
                <tr style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  <td colSpan={consolidated ? 7 : 6} style={{ textAlign: 'right' }}>
                    Opening balance
                    {data.from ? ` as of ${formatAccountingDate(data.from)}` : ''}
                  </td>
                  <td></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(data.openingBalance, true)}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td colSpan={consolidated ? 6 : 5}>Total ({data.lines.length} lines)</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(data.totals.debit)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(data.totals.credit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
